import { PlusOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, Popover, Space, Table, Tabs, TabsProps } from "antd";
import axios from "axios";
import classNames, { type Argument } from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { getAuthorizationHeader } from "../../utils/auth";
import { getApiBaseUrl } from "../../utils/misc";

// Types
interface ProjectData {
  campaign_name: string;
  created: string;
  created_by_email: string;
  id: number;
}

interface InterviewRoomData {
  id: number;
  name: string;
  project_name: string;
  project_link?: string;
  status: string;
  links?: {
    moderator?: string;
  };
}

interface SearchInterviewRoomsProjectsProps {
  className?: Argument;
  disabled?: boolean;
  style?: React.CSSProperties;
  onClose?: () => void;
  studyId?: string;
  onSelectLink?: (link: string) => void;
}

interface PaginationState {
  page: number;
  total: number;
}

const useInterviewRooms = (studyId?: string) => {
  const { message } = App.useApp();
  const [data, setData] = useState<InterviewRoomData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, total: 0 });
  const [search, setSearch] = useState("");

  const fetchInterviewRooms = useCallback(
    async (searchValue: string, pageOverride?: number) => {
      if (!studyId) return [];

      setLoading(true);
      try {
        const pageToUse = pageOverride !== undefined ? pageOverride : pagination.page;
        const url = `${getApiBaseUrl()}/interviewroom_list?study_id=${studyId}&page=${pageToUse}&per_page=10&order_direction=desc`;
        const fullUrl = searchValue ? `${url}&name=${encodeURIComponent(searchValue)}` : url;

        const response = await axios.get(fullUrl, { headers: getAuthorizationHeader() });

        let roomsArray = [];
        if (response.data && typeof response.data === "object") {
          if (Array.isArray(response.data)) {
            roomsArray = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            roomsArray = response.data.data;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            roomsArray = response.data.items;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            roomsArray = response.data.results;
          } else {
            console.log("Unexpected API response structure:", response.data);
          }
        }

        setPagination({
          page: pageToUse,
          total: response.data.total,
        });

        setData(roomsArray);
        return roomsArray;
      } catch (error) {
        console.error("Error fetching interview rooms:", error);
        message.error("Failed to fetch interview rooms");
        setData([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [studyId, pagination.page, message]
  );

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const executeSearch = useCallback(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchInterviewRooms(search, 1);
  }, [fetchInterviewRooms, search]);

  const handlePaginationChange = useCallback(
    (page: number) => {
      setPagination(prev => ({ ...prev, page }));
      fetchInterviewRooms(search, page);
    },
    [fetchInterviewRooms, search]
  );

  return {
    data,
    loading,
    pagination,
    search,
    handleSearch,
    executeSearch,
    handlePaginationChange,
    fetchInterviewRooms,
  };
};

const useProjects = () => {
  const { message } = App.useApp();
  const [data, setData] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, total: 0 });
  const [search, setSearch] = useState("");

  const fetchProjects = useCallback(
    async (searchValue: string, pageOverride?: number) => {
      console.log("fetchProjects", searchValue);
      setLoading(true);
      try {
        const pageToUse = pageOverride !== undefined ? pageOverride : pagination.page;
        const url = `${getApiBaseUrl()}/interviewroom_projects_list?page=${pageToUse}&per_page=10&order_direction=desc`;
        const fullUrl = searchValue ? `${url}&project_name=${encodeURIComponent(searchValue)}` : url;

        const response = await axios.get(fullUrl, { headers: getAuthorizationHeader() });

        let projectsArray = [];
        if (response.data && typeof response.data === "object") {
          if (Array.isArray(response.data)) {
            projectsArray = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            projectsArray = response.data.data;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            projectsArray = response.data.items;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            projectsArray = response.data.results;
          } else {
            console.log("Unexpected API response structure:", response.data);
          }
        }

        setPagination({
          page: pageToUse,
          total: response.data.total,
        });

        setData(projectsArray);
        return projectsArray;
      } catch (error) {
        console.error("Error fetching projects:", error);
        message.error("Failed to fetch projects");
        setData([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, message]
  );

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const executeSearch = useCallback(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchProjects(search, 1);
  }, [fetchProjects, search]);

  const handlePaginationChange = useCallback(
    (page: number) => {
      setPagination(prev => ({ ...prev, page }));
      fetchProjects(search, page);
    },
    [fetchProjects, search]
  );

  return {
    data,
    loading,
    pagination,
    search,
    handleSearch,
    executeSearch,
    handlePaginationChange,
    fetchProjects,
  };
};

const useRoomCreation = (onSelectLink?: (link: string) => void, onClose?: () => void, onSuccess?: () => void) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  const extractModeratorLink = (responseData: any): string | null => {
    if (responseData?.rooms?.[0]?.links?.moderator) {
      return responseData.rooms[0].links.moderator;
    } else if (responseData?.data?.rooms?.[0]?.links?.moderator) {
      return responseData.data.rooms[0].links.moderator;
    } else if (responseData?.links?.moderator) {
      return responseData.links.moderator;
    }
    return null;
  };

  const createRoom = async (projectId: number, roomName: string) => {
    if (!roomName.trim()) {
      message.error("Room name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${getApiBaseUrl()}/interviewroom_add_rooms_to_project`,
        {
          question_id: projectId,
          rooms: [{ name: roomName }],
        },
        { headers: getAuthorizationHeader() }
      );

      message.success("Interview room created successfully");

      const responseData = response.data;
      let moderatorLink = extractModeratorLink(responseData);

      if (moderatorLink && onSelectLink) {
        onSelectLink(moderatorLink);
        onClose?.();
      } else {
        console.warn("Moderator link not found in response:", responseData);
        onSuccess?.();
      }

      return response.data;
    } catch (error) {
      console.error("Error creating interview room:", error);
      message.error("Failed to create interview room");
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectName: string) => {
    if (!projectName.trim()) {
      message.error("Project name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/interviewroom_create_project`,
        {
          project_name: projectName,
          rooms: [{ name: projectName }],
        },
        { headers: getAuthorizationHeader() }
      );

      message.success("Project and interview room created successfully");

      const responseData = response.data;
      const moderatorLink = responseData?.rooms?.[0]?.links?.moderator || null;

      if (moderatorLink && onSelectLink) {
        onSelectLink(moderatorLink);
        onClose?.();
      } else {
        onSuccess?.();
      }

      return responseData;
    } catch (error) {
      console.error("Error creating project:", error);
      message.error("Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createRoom,
    createProject,
  };
};

const InterviewRoomsTable: React.FC<{
  data: InterviewRoomData[];
  loading: boolean;
  pagination: PaginationState;
  searchValue: string;
  onSearch: (value: string) => void;
  onSearchExecute: () => void;
  onPaginationChange: (page: number) => void;
  onSelectLink?: (link: string) => void;
  onClose?: () => void;
  fetchInterviewRooms: (searchValue: string, pageOverride?: number) => Promise<InterviewRoomData[]>;
}> = ({
  data,
  loading,
  pagination,
  searchValue,
  onSearch,
  onSearchExecute,
  onPaginationChange,
  onSelectLink,
  onClose,
  fetchInterviewRooms,
}) => {
  const { message } = App.useApp();

  const handleSearch = (value: string) => {
    onSearch(value);
    onSearchExecute();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onSearch(newValue);

    if (newValue === "" && searchValue !== "") {
      fetchInterviewRooms(newValue, 1);
    }
  };

  const columns = [
    {
      title: "Room Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Project Name",
      dataIndex: "project_name",
      key: "project_name",
      render: (text: string, record: InterviewRoomData) => {
        return record.project_link ? (
          <a href={record.project_link} target="_blank" rel="noreferrer noopener">
            {text}
          </a>
        ) : (
          text || "No project name"
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: InterviewRoomData) => (
        <Button
          key={`select-link-${record.id}`}
          type="primary"
          size="small"
          disabled={!record.links?.moderator}
          onClick={() => {
            if (record.links?.moderator && onSelectLink) {
              onSelectLink(record.links.moderator);
              onClose?.();
            } else {
              message.warning("No link available for this room");
            }
          }}
        >
          {record.links?.moderator ? "Select Link" : "No Link"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          marginBottom: 24,
          padding: 16,
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          backgroundColor: "#f2f3f5",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: 10 }}>Search for an existing interview room</h4>
        <div className="search-container" style={{ marginBottom: 0 }}>
          <Input.Search
            placeholder="Search interview rooms..."
            onChange={handleChange}
            onSearch={handleSearch}
            value={searchValue}
            allowClear
            enterButton
          />
        </div>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          total: pagination.total,
          current: pagination.page,
          onChange: onPaginationChange,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: searchValue ? "No interview rooms found" : "Search for interview rooms above",
        }}
      />
    </>
  );
};

const RoomCreationForm: React.FC<{
  recordId: number;
  defaultName: string;
  onCancel: () => void;
  onSubmit: (name: string) => Promise<void>;
  loading?: boolean;
}> = ({ recordId, defaultName, onCancel, onSubmit, loading }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({ roomName: defaultName });
  }, [form, defaultName]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(values => {
        onSubmit(values.roomName);
      })
      .catch(info => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <div key={`popover-content-${recordId}`} style={{ padding: "8px 0" }} onClick={e => e.stopPropagation()}>
      <Form form={form} layout="vertical" initialValues={{ roomName: defaultName }} onFinish={handleSubmit}>
        <Form.Item name="roomName" rules={[{ required: true, message: "Please enter a room name" }]}>
          <Input placeholder="Enter room name" style={{ width: 240 }} onPressEnter={handleSubmit} autoFocus />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button type="primary" size="small" onClick={handleSubmit} loading={loading}>
              Create
            </Button>
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

const ProjectsTable: React.FC<{
  data: ProjectData[];
  loading: boolean;
  pagination: PaginationState;
  searchValue: string;
  onSearch: (value: string) => void;
  onSearchExecute: () => void;
  onPaginationChange: (page: number) => void;
  onSelectLink?: (link: string) => void;
  onClose?: () => void;
  onSuccessfulCreation?: () => void;
  fetchProjects: (searchValue: string, pageOverride?: number) => Promise<ProjectData[]>;
}> = ({
  data,
  loading,
  pagination,
  searchValue,
  onSearch,
  onSearchExecute,
  onPaginationChange,
  onSelectLink,
  onClose,
  onSuccessfulCreation,
  fetchProjects,
}) => {
  const [newProjectForm] = Form.useForm();
  const [popoverVisible, setPopoverVisible] = useState<Record<string, boolean>>({});

  const {
    loading: creatingLoading,
    createRoom,
    createProject,
  } = useRoomCreation(onSelectLink, onClose, onSuccessfulCreation);

  const handleSearch = (value: string) => {
    onSearch(value);
    onSearchExecute();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onSearch(newValue);
    if (newValue === "" && searchValue !== "") {
      fetchProjects(newValue, 1);
    }
  };

  const handleCreateProject = async () => {
    try {
      const values = await newProjectForm.validateFields();
      await createProject(values.projectName);
      newProjectForm.resetFields();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const togglePopover = (recordId: number, visible: boolean) => {
    if (popoverVisible[recordId] !== visible) {
      setPopoverVisible(prev => ({
        ...prev,
        [recordId]: visible,
      }));
    }
  };

  const columns = [
    {
      title: "Project Name",
      dataIndex: "campaign_name",
      key: "campaign_name",
    },
    {
      title: "Created Date",
      dataIndex: "created",
      key: "created",
      render: (text: string) => {
        const date = new Date(text);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by_email",
      key: "created_by_email",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: ProjectData) => {
        const defaultRoomName = `${record.campaign_name} Room`;

        return (
          <Popover
            content={
              <RoomCreationForm
                recordId={record.id}
                defaultName={defaultRoomName}
                onCancel={() => togglePopover(record.id, false)}
                onSubmit={async name => {
                  await createRoom(record.id, name);
                  togglePopover(record.id, false);
                }}
                loading={creatingLoading}
              />
            }
            title="Enter Room Name"
            trigger="click"
            open={popoverVisible[record.id]}
            onOpenChange={visible => togglePopover(record.id, visible)}
            destroyTooltipOnHide={true}
            getPopupContainer={triggerNode => triggerNode.parentElement || document.body}
          >
            <Button
              key={`create-room-${record.id}`}
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={e => {
                e.stopPropagation();
                togglePopover(record.id, true);
              }}
            >
              Create Room
            </Button>
          </Popover>
        );
      },
    },
  ];

  return (
    <>
      <div
        className="create-project-container"
        style={{
          marginBottom: 24,
          padding: 16,
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          backgroundColor: "#f2f3f5",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: 10 }}>Create a new project</h4>
        <Form form={newProjectForm} layout="inline" style={{ width: "100%" }} onFinish={handleCreateProject}>
          <Form.Item
            name="projectName"
            rules={[{ required: true, message: "Please enter a project name" }]}
            style={{ flex: "1 1 auto", marginRight: 8, marginBottom: 0 }}
          >
            <Input placeholder="Project Name" disabled={creatingLoading} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={creatingLoading}>
              Create Project
            </Button>
          </Form.Item>
        </Form>
      </div>

      <div
        style={{
          marginBottom: 24,
          padding: 16,
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          backgroundColor: "#f2f3f5",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: 10 }}>Add to an existing project</h4>
        <div className="search-container" style={{ marginBottom: 0 }}>
          <Input.Search
            placeholder="Search projects..."
            onChange={handleChange}
            onSearch={handleSearch}
            value={searchValue}
            allowClear
            enterButton
          />
        </div>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          total: pagination.total,
          current: pagination.page,
          onChange: onPaginationChange,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: searchValue ? "No projects found" : "Search for projects above",
        }}
      />
    </>
  );
};

export const SearchInterviewRoomsProjects: React.FC<SearchInterviewRoomsProjectsProps> = ({
  className,
  disabled = false,
  style,
  onClose,
  studyId,
  onSelectLink,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("2");

  const interviewRoomState = useInterviewRooms(studyId);
  const projectState = useProjects();

  const handleModalOpen = () => {
    setIsModalOpen(true);
    setActiveTab("2");

    interviewRoomState.handleSearch("");
    projectState.handleSearch("");
    interviewRoomState.executeSearch();
    projectState.executeSearch();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (onClose) onClose();
  };

  const handleProjectCreationSuccess = useCallback(() => {
    projectState.handleSearch("");
    projectState.executeSearch();
    setActiveTab("1");
  }, [projectState]);

  const tabItems: TabsProps["items"] = [
    {
      key: "2",
      label: "Projects",
      children: (
        <div className="tab-content">
          <ProjectsTable
            data={projectState.data}
            loading={projectState.loading}
            pagination={projectState.pagination}
            searchValue={projectState.search}
            onSearch={projectState.handleSearch}
            onSearchExecute={projectState.executeSearch}
            onPaginationChange={projectState.handlePaginationChange}
            onSelectLink={onSelectLink}
            onClose={handleModalClose}
            onSuccessfulCreation={handleProjectCreationSuccess}
            fetchProjects={projectState.fetchProjects}
          />
        </div>
      ),
    },
    {
      key: "1",
      label: "Interview Rooms",
      children: (
        <div className="tab-content">
          <InterviewRoomsTable
            data={interviewRoomState.data}
            loading={interviewRoomState.loading}
            pagination={interviewRoomState.pagination}
            searchValue={interviewRoomState.search}
            onSearch={interviewRoomState.handleSearch}
            onSearchExecute={interviewRoomState.executeSearch}
            onPaginationChange={interviewRoomState.handlePaginationChange}
            onSelectLink={onSelectLink}
            onClose={handleModalClose}
            fetchInterviewRooms={interviewRoomState.fetchInterviewRooms}
          />
        </div>
      ),
    },
  ];

  return (
    <StyledContainer className={classNames("search-interview-rooms-projects", className)} style={style}>
      <Button
        type="link"
        onClick={handleModalOpen}
        disabled={disabled}
        style={{ padding: 0, marginBottom: 10, height: "auto" }}
      >
        Host with Voxpopme
      </Button>

      <Modal
        title="Search Interview Rooms & Projects"
        open={isModalOpen}
        onCancel={handleModalClose}
        width={1000}
        footer={null}
        destroyOnClose
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Modal>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  .tab-content {
    padding: 16px;
    min-height: 300px;
  }

  .ant-tabs-nav {
    margin-bottom: 16px;
  }

  .search-container {
    margin-bottom: 16px;
  }
`;

export default SearchInterviewRoomsProjects;
